import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Box,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { type BoundaryRecord } from './BoundaryImporterView';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface FormData {
  pos: string;
  easting: string;
  northing: string;
  elevation: string;
}

interface FormErrors {
  pos?: string;
  easting?: string;
  northing?: string;
  elevation?: string;
}

interface Props {
  open: boolean;
  boundary: BoundaryRecord | null;
  onClose: () => void;
  onSave: (updated: Omit<BoundaryRecord, 'id'>) => Promise<void>;
}

const SectionLabel = ({ label }: { label: string }) => (
  <Box
    sx={{
      mb: 1.5,
      pb: 0.5,
      borderBottom: '1px solid',
      borderColor: (t) => alpha(t.palette.divider, 0.6),
    }}
  >
    <Typography
      variant="caption"
      fontWeight={700}
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
    >
      {label}
    </Typography>
  </Box>
);

export const EditBoundaryModal = ({
  open,
  boundary,
  onClose,
  onSave,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormData>({
    pos: '',
    easting: '',
    northing: '',
    elevation: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (boundary) {
      setForm({
        pos: String(boundary.pos),
        easting: String(boundary.easting),
        northing: String(boundary.northing),
        elevation: String(boundary.elevation),
      });
      setErrors({});
    }
  }, [boundary]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!form.pos.trim()) {
      next.pos = t('boundary_importer_err_required');
    } else {
      const posNum = parseInt(form.pos, 10);
      if (isNaN(posNum) || posNum <= 0 || !Number.isInteger(Number(form.pos))) {
        next.pos = t('boundary_importer_err_positive_int');
      }
    }
    if (!form.easting.trim()) {
      next.easting = t('boundary_importer_err_required');
    } else if (isNaN(parseFloat(form.easting))) {
      next.easting = t('boundary_importer_err_number');
    }
    if (!form.northing.trim()) {
      next.northing = t('boundary_importer_err_required');
    } else if (isNaN(parseFloat(form.northing))) {
      next.northing = t('boundary_importer_err_number');
    }
    if (!form.elevation.trim()) {
      next.elevation = t('boundary_importer_err_required');
    } else if (isNaN(parseFloat(form.elevation))) {
      next.elevation = t('boundary_importer_err_number');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const updated: Omit<BoundaryRecord, 'id'> = {
      gradeName: boundary?.gradeName ?? '',
      locationName: boundary?.locationName ?? '',
      pos: parseInt(form.pos, 10),
      easting: parseFloat(form.easting),
      northing: parseFloat(form.northing),
      elevation: parseFloat(form.elevation),
    };

    setSubmitting(true);
    try {
      await onSave(updated);
    } catch {
      // error already toasted by parent; modal stays open
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    (key: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const palette = clientThemePalette[CLIENT_THEME].light;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (t) => alpha(t.palette.primary.main, 0.15),
          backgroundImage: 'none',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.primary.main, 0.25),
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {t('boundary_importer_edit_title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {boundary ? `${boundary.locationName} #${boundary.pos}` : ''}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* ── Body ── */}
      <DialogContent sx={{ px: 3, pt: 1, pb: 2, overflowY: 'auto', flex: 1 }}>
        {/* Boundary Info */}
        <SectionLabel label={t('boundary_importer_section_info')} />
        <Box
          sx={{
            mb: 3,
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
            bgcolor: (t) => alpha(t.palette.action.selected, 0.5),
            border: '1px solid',
            borderColor: (t) => alpha(t.palette.divider, 0.4),
            display: 'flex',
            gap: 4,
          }}
        >
          {boundary?.gradeName && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('boundary_importer_col_grade_name')}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {boundary.gradeName}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('boundary_importer_field_location_name')}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {boundary?.locationName ?? '—'}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              label={t('boundary_importer_field_pos')}
              fullWidth
              size="small"
              type="number"
              value={form.pos}
              onChange={field('pos')}
              error={!!errors.pos}
              helperText={errors.pos}
              inputProps={{ min: 1, step: 1 }}
            />
          </Grid>
        </Grid>

        {/* Coordinates */}
        <SectionLabel label={t('boundary_importer_section_coords')} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label={t('boundary_importer_field_easting')}
              fullWidth
              size="small"
              type="number"
              value={form.easting}
              onChange={field('easting')}
              error={!!errors.easting}
              helperText={errors.easting}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label={t('boundary_importer_field_northing')}
              fullWidth
              size="small"
              type="number"
              value={form.northing}
              onChange={field('northing')}
              error={!!errors.northing}
              helperText={errors.northing}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label={t('boundary_importer_field_elevation')}
              fullWidth
              size="small"
              type="number"
              value={form.elevation}
              onChange={field('elevation')}
              error={!!errors.elevation}
              helperText={errors.elevation}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          flexShrink: 0,
          borderTop: '1px solid',
          borderColor: (t) => alpha(t.palette.divider, 0.5),
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={submitting}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('boundary_importer_cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
          sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
        >
          {t('boundary_importer_save_changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
